// import i18n from '@/src/i18n';

import type {
	ContentClass,
	PostSingleVariations,
	SectionDecorationOptions,
} from '@Types/Shared/Common';
import type { QueryPost } from '@Types/Shared/Query';

import type { SocialsElements } from '@/src/Types/Shared/Footer';
import type { ImgFormatVariations } from '@/src/Types/Shared/Image';
import Html from '@/src/Views/Shared/Html';
import { __Front } from '@/src/Views/Shared/lib';

type SingleBlockProps = {
	post: QueryPost;
	variant?: PostSingleVariations;
	singleHeadingFontClassName?: ContentClass;
	context: string;
	contentFontFormats?: ContentClass;
	imgType?: ImgFormatVariations;
	sectionDecoration?: SectionDecorationOptions;
	displayFunction?: boolean;
	displayMail?: boolean;
	displayPhone?: boolean;
	displaySocials?: boolean;
};

export default function SingleBlock(props: SingleBlockProps) {
	const {
		post,
		variant = 'block',
		singleHeadingFontClassName,
		context,
		contentFontFormats,
		sectionDecoration,
		imgType = 'squared',
		displayFunction,
		displayMail,
		displayPhone,
		displaySocials,
	} = props;

	const socialsClass =
		'minos-single-teams-social text-2xl tablet:minos-single-teams-social hover:text-minos-single-teams-social-color-hover transition-all ease-in';

	return (
		<>
			<div className='minos-grid bg-minos-single-teams-secondary-color py-5 tablet:py-12'>
				<div
					data-variant={`single-${variant}`}
					className={`relative z-[11] col-start-2 col-end-14 tablet:col-span-12 tablet:col-start-2 desktop:col-span-12 desktop:col-start-2`}
				>
					{post.image && post.image.url ? (
						<img
							className={`mx-auto mb-7 block aspect-square w-[384px] object-cover tablet:mb-10 desktop:mb-12 ${imgType === 'squared' ? 'rounded-minos-single-teams-img' : 'rounded-full'}`}
							src={post.image.url}
							alt=''
						/>
					) : (
						''
					)}
					<div className='rounded-minos-single-teams-content border-minos-single-teams-content-border-width border-minos-single-teams-content-border-color bg-minos-single-teams-content-color p-5'>
						<h1
							className={`text-center ${singleHeadingFontClassName?.title} ${(displayFunction || displaySocials || displayMail || displayPhone) && 'mb-4 tablet:mb-6'}`}
						>
							{post.firstname && post.lastname ? post.firstname + ' ' + post.lastname : post.post_title}
						</h1>

						{(displayFunction || displaySocials || displayMail || displayPhone) && (
							<div className={`mb-5 tablet:mb-6 desktop:mb-12  ${singleHeadingFontClassName?.text?.p}`}>
								<div className='flex flex-col items-center gap-4 tablet:gap-6'>
									{displayFunction && post.tag && post.tag?.length ? (
										<>
											{post.tag.length > 1 ? (
												<ul className='flex flex-wrap gap-3'>
													{post.tag.map((value: string, index: number) => (
														<li key={index}>{value + (index + 1 === (post.tag?.length ?? 0) ? '' : ', ')}</li>
													))}
												</ul>
											) : (
												<p className='flex flex-wrap gap-3'>{post.tag[0]}</p>
											)}
										</>
									) : (
										''
									)}

									{displaySocials && post.socials && post.socials?.length > 0 && (
										<>
											<div className='mt-2 flex flex-wrap gap-5 tablet:mt-0 tablet:gap-3'>
												{post.socials?.map((social: SocialsElements, i: number) => (
													<a
														key={i}
														href={social.url}
														className={`${socialsClass}`}
														aria-label={`${__Front('front.socials')} ${social.name ? social.name : social.icon ? social.icon : ''} - ${__Front('front.openNewTab')}`}
														title={`${__Front('front.socials')} ${social.name ? social.name : social.icon ? social.icon : ''} - ${__Front('front.openNewTab')}`}
														target='_blank'
														rel='noreferrer'
													>
														{social.icon && <i className={social.icon}></i>}
													</a>
												))}
											</div>
										</>
									)}
									<div className='text-center'>
										{displayPhone && (
											<>
												{post.phoneInfos && post.phoneInfos.phone && (
													<>
														<p className='mb-1'>
															<a
																className='group inline-flex items-center gap-5 transition-all ease-in hover:text-minos-single-teams-social-color-hover tablet:gap-3'
																href={'tel:' + post.phoneInfos.phone}
															>
																{post.phoneInfos.icon && (
																	<i
																		aria-hidden
																		className={`${post.phoneInfos.icon} ${socialsClass} group-hover:text-minos-single-teams-social-color-hover`}
																	></i>
																)}
																{post.phoneInfos.phone}
															</a>
														</p>
													</>
												)}
											</>
										)}

										{displayMail && (
											<>
												{post.emailInfos && post.emailInfos.email && (
													<>
														<p>
															<a
																className='group inline-flex items-center gap-5 transition-all ease-in hover:text-minos-single-teams-social-color-hover tablet:gap-3'
																href={'mailto:' + post.emailInfos.email}
															>
																{post.emailInfos.icon && (
																	<i
																		aria-hidden
																		className={`${post.emailInfos.icon} ${socialsClass} group-hover:text-minos-single-teams-social-color-hover`}
																	></i>
																)}
																{post.emailInfos.email}
															</a>
														</p>
													</>
												)}
											</>
										)}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			<div
				className={`minos-grid bg-minos-single-teams-bg-color ${sectionDecoration?.isSectionDecorated && sectionDecoration?.type === 'wave' ? 'pb-10 pt-5 tablet:pb-24 tablet:pt-12 ' : 'py-5 tablet:py-12'}`}
			>
				<div className='minos-container col-span-12 col-start-2 tablet:col-span-8 tablet:col-start-4'>
					<div data-content='minos-content' className={contentFontFormats?.text?.p}>
						<Html context={context} className={contentFontFormats?.text} html={post.post_content} />
					</div>
				</div>
			</div>
		</>
	);
}
